import { finalPolicyLabel, outcomeDisplayLabel } from "./attemptResults";

const safeText = (value) => value === null || value === undefined ? "" : String(value);

const excelSafeText = (value) => {
  const text = safeText(value);
  return /^[=+\-@]/.test(text) ? `'${text}` : text;
};

const csvCell = (value) => `"${excelSafeText(value).replace(/"/g, '""')}"`;

const finite = (value) => Number.isFinite(value) ? value : "";

const percentage = (value) => Number.isFinite(value) ? `${Number(value).toFixed(2)}%` : "";

const questionId = (value) => safeText(value).split("/").pop();

const attemptStatus = (attempt = {}) => attempt.status === "in_progress"
  ? "In progress"
  : attempt.outcome?.status === "complete" ? "Completed" : "Marking in progress";

const contribution = (attempt, finalResult, completedAttempts, fallbackSelectedAttemptId) => {
  if (attempt.outcome?.status !== "complete") return "";
  if (finalResult?.policy === "average") return "Included in average";
  if (finalResult) {
    const selected = finalResult.selectedAttemptId === attempt.id || (
      !finalResult.selectedAttemptId &&
      Number.isSafeInteger(finalResult.selectedAttemptNumber) &&
      finalResult.selectedAttemptNumber === attempt.attemptNumber
    );
    return selected ? "Counts as assessment result" : "Not selected";
  }
  const selected = fallbackSelectedAttemptId
    ? fallbackSelectedAttemptId === attempt.id
    : completedAttempts.length === 1;
  return selected ? "Counts as assessment result" : "Not selected";
};

export const sanitizeResultsFilenamePart = (value, fallback = "Assessment") => safeText(value)
  .normalize("NFKD")
  .replace(/[^a-zA-Z0-9 _-]/g, "")
  .trim()
  .replace(/\s+/g, "_")
  .slice(0, 80) || fallback;

export const assessmentResultsCsvFilename = (assessmentTitle) =>
  `StudyDen_${sanitizeResultsFilenamePart(assessmentTitle)}_Results.csv`;

export function buildAssessmentResultsCsv({
  assessmentTitle = "",
  subject = "",
  quizCode = "",
  questionIds = [],
  assessmentSeries = [],
} = {}) {
  const orderedQuestionIds = questionIds.map(questionId).filter(Boolean);
  const coreHeaders = [
    "Student Name",
    "Student Email",
    "Assessment Title",
    "Subject",
    "Test Code",
    "Attempt Number",
    "Attempt Status",
    "Attempt Score",
    "Attempt Maximum",
    "Attempt Percentage",
    "Attempt Outcome",
    "Assessment Result Percentage",
    "Assessment Outcome",
    "Result Method",
    "Completed Attempt Count",
    "Result Contribution",
    "Completed At",
  ];
  const questionHeaders = orderedQuestionIds.flatMap((_, index) => [
    `Q${index + 1} Mark`,
    `Q${index + 1} Maximum`,
  ]);
  const rows = [[...coreHeaders, ...questionHeaders]];

  [...assessmentSeries]
    .sort((left, right) => safeText(left.studentName || left.records?.[0]?.username)
      .localeCompare(safeText(right.studentName || right.records?.[0]?.username)))
    .forEach((series) => {
      const attempts = [...(series.attempts || [])].sort((left, right) =>
        Number(left.attemptNumber || 1) - Number(right.attemptNumber || 1)
      );
      const completedAttempts = attempts.filter(({ outcome }) => outcome?.status === "complete");
      const finalResult = series.finalResult || null;
      const canonical = finalResult || series.summary || completedAttempts.at(-1)?.outcome || null;
      const representative = series.records?.[0] || attempts.flatMap(({ records }) => records || [])[0] || {};
      const studentName = series.studentName || representative.username || "Unknown Student";
      const studentEmail = series.studentEmail || representative.email || "";
      const resultMethod = finalResult ? finalPolicyLabel(finalResult.policy) : "Assessment result";
      const completedAttemptCount = Number.isFinite(finalResult?.completedAttemptCount)
        ? finalResult.completedAttemptCount
        : completedAttempts.length;

      attempts.forEach((attempt) => {
        const complete = attempt.outcome?.status === "complete";
        const recordsByQuestion = new Map((attempt.records || []).map((record) => [questionId(record.questionId), record]));
        const questionCells = orderedQuestionIds.flatMap((id) => {
          const record = recordsByQuestion.get(id);
          const awarded = record && record.markAwarded !== "Pending" && record.markAwarded !== "N/A"
            ? finite(Number(record.markAwarded)) : "";
          return [awarded, record ? finite(Number(record.maxMark)) : ""];
        });
        rows.push([
          studentName,
          studentEmail,
          assessmentTitle,
          subject,
          quizCode,
          Number(attempt.attemptNumber) || 1,
          attemptStatus(attempt),
          complete ? finite(attempt.outcome.totalAwarded) : "",
          complete ? finite(attempt.outcome.totalPossible) : "",
          complete ? percentage(attempt.outcome.percentage) : "",
          complete ? outcomeDisplayLabel(attempt.outcome.achievedOutcomeLabel) : "",
          percentage(canonical?.percentage),
          outcomeDisplayLabel(canonical?.achievedOutcomeLabel),
          resultMethod,
          completedAttemptCount,
          contribution(attempt, finalResult, completedAttempts, series.selectedAttemptId),
          attempt.submittedAt || "",
          ...questionCells,
        ]);
      });
    });

  return `\uFEFF${rows.map((row) => row.map(csvCell).join(",")).join("\r\n")}`;
}
